import { useTranslation } from 'react-i18next';
import { useBudgetStats, useBudgetsApproachingLimit, useBudgetsExceeded } from '../../hooks/useBudgets';
import { BudgetUtilizationChart } from '../reports/Charts';

interface BudgetDashboardProps {
  fiscalYear?: number;
}

export default function BudgetDashboard({ fiscalYear }: BudgetDashboardProps) {
  const { t } = useTranslation();
  const { data: stats, isLoading: statsLoading } = useBudgetStats(fiscalYear);
  const { data: approachingLimit } = useBudgetsApproachingLimit(80);
  const { data: exceeded } = useBudgetsExceeded();

  if (statsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const utilizationPercent = stats.utilizationRate || 0;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="rounded-md bg-blue-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('budgets.totalAllocated', 'Total Alloué')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.totalAllocated.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} TND
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="rounded-md bg-orange-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('budgets.totalSpent', 'Total Dépensé')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.totalSpent.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} TND
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="rounded-md bg-green-500 p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('budgets.available', 'Disponible')}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.totalAvailable.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} TND
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`rounded-md p-3 ${utilizationPercent >= 90 ? 'bg-red-500' : utilizationPercent >= 70 ? 'bg-yellow-500' : 'bg-blue-500'}`}>
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('budgets.utilizationRate', 'Taux d\'utilisation')}</p>
              <p className="text-2xl font-semibold text-gray-900">{utilizationPercent.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Utilization Progress Bar */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('budgets.globalUtilization', 'Utilisation Globale')}</h3>
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${
                utilizationPercent >= 100 ? 'text-red-600 bg-red-200' :
                utilizationPercent >= 80 ? 'text-yellow-600 bg-yellow-200' :
                'text-green-600 bg-green-200'
              }`}>
                {utilizationPercent >= 100 ? t('budgets.exceeded', 'Dépassé') :
                 utilizationPercent >= 80 ? t('budgets.warning', 'Attention') :
                 t('budgets.normal', 'Normal')}
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold inline-block text-gray-600">
                {stats.totalSpent.toLocaleString('fr-FR')} / {stats.totalAllocated.toLocaleString('fr-FR')} TND
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-gray-200">
            <div
              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
                utilizationPercent >= 100 ? 'bg-red-500' :
                utilizationPercent >= 80 ? 'bg-yellow-500' :
                'bg-green-500'
              }`}
            ></div>
          </div>
        </div>
      </div>

      {/* Budget Alerts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Budgets Approaching Limit */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 text-yellow-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {t('budgets.approachingLimit', 'Budgets proches du seuil')}
          </h3>
          {approachingLimit && approachingLimit.items.length > 0 ? (
            <div className="space-y-3">
              {approachingLimit.items.slice(0, 5).map((budget: any) => {
                const percent = budget.total_amount > 0
                  ? ((budget.total_spent || 0) / budget.total_amount) * 100
                  : 0;
                return (
                  <div key={budget.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{budget.name}</p>
                      <p className="text-sm text-gray-500">{budget.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-yellow-600">{percent.toFixed(1)}%</p>
                      <p className="text-xs text-gray-500">
                        {(budget.total_spent || 0).toLocaleString('fr-FR')} / {budget.total_amount.toLocaleString('fr-FR')} TND
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">{t('budgets.noApproachingLimit', 'Aucun budget proche du seuil')}</p>
          )}
        </div>

        {/* Exceeded Budgets */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('budgets.exceededBudgets', 'Budgets dépassés')}
          </h3>
          {exceeded && exceeded.items.length > 0 ? (
            <div className="space-y-3">
              {exceeded.items.slice(0, 5).map((budget: any) => {
                const overage = (budget.total_spent || 0) - budget.total_amount;
                return (
                  <div key={budget.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{budget.name}</p>
                      <p className="text-sm text-gray-500">{budget.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">+{overage.toLocaleString('fr-FR')} TND</p>
                      <p className="text-xs text-gray-500">{t('budgets.overBudget', 'Dépassement')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">{t('budgets.noExceeded', 'Aucun budget dépassé')}</p>
          )}
        </div>
      </div>

      {/* Budget by Status */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('budgets.byStatus', 'Par Statut')}</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Object.entries(stats.byStatus).map(([status, count]) => (
            <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className="text-sm text-gray-500 capitalize">{t(`budgets.statuses.${status}`, status)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Budget Committed */}
      {stats.totalCommitted > 0 && (
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('budgets.committed', 'Engagé (Non Dépensé)')}</h3>
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-3xl font-bold text-purple-600">
                {stats.totalCommitted.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} TND
              </p>
              <p className="text-sm text-gray-500">{t('budgets.committedDescription', 'Montant réservé pour des commandes en cours')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
